import { Box, Typography, CircularProgress, Divider } from "@mui/material";
import React, { useEffect, useState } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import ReactMarkdown from "react-markdown";
import { getLatestPrivacyPolicy } from "src/api/getLatestPrivacyPolicy";
import styles from "../../content/pages/homepage.module.css";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LegalFooter from "../LegalFooter/LegalFooter";

const PrivacyPolicy = () => {
  const [policyText, setPolicyText] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const fetchPolicy = async () => {
    try {
      const latestPolicy = await getLatestPrivacyPolicy();
      setPolicyText(latestPolicy.text);
    } catch (error) {
      console.error("Failed to fetch privacy policy:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, []);

  return (
    <Box
      className={styles.appRoot}
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "#000000",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
      <Box sx={{ maxWidth: { lg: "1200px", xs: "auto" }, m: "auto", pt: 3, pb: 2 }}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-around"
          alignItems="center"
        >
          <Typography
            fontSize={{ xs: "24px", md: "40px" }}
            width={{ xs: 300, md: 500 }}
            fontWeight={400}
            color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
            lineHeight={1.2}
            fontFamily="Oswald, Arial"
            textAlign="center"
            mb={4}
          >
            Privacy Policy
          </Typography>

          <Box sx={{ maxWidth: "900px", width: "100%", px: 2 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" my={5}>
                <CircularProgress size={64} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
              </Box>
            ) : (
              <Box
                sx={{
                  "& h1, & h2, & h3, & h4, & h5, & h6": {
                    fontFamily: "Oswald, Arial",
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    fontWeight: 700,
                    fontSize: { xs: "16px", md: "20px" },
                    mb: 1,
                    mt: 0,
                  },
                  "& p": {
                    fontFamily: "Roboto, Arial",
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    fontSize: { xs: "14px", md: "16px" },
                    lineHeight: 1.5,
                    fontWeight: 700,
                    opacity: 0.9,
                    m: 0,
                    mb: 2,
                  },
                  "& span": {
                    fontFamily: "Roboto, Arial",
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    fontSize: { xs: "14px", md: "16px" },
                    lineHeight: 1.5,
                    fontWeight: 700,
                    opacity: 1,
                    m: 0,
                    mb: 1,
                  },
                  "& ul": {
                    fontFamily: "Roboto, Arial",
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    fontSize: { xs: "14px", md: "16px" },
                    lineHeight: 1.5,
                    fontWeight: 700,
                    opacity: 0.9,
                    m: 0,
                    mb: 0,
                  },
                  "& li": {
                    fontFamily: "Roboto, Arial",
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    fontSize: { xs: "14px", md: "16px" },
                    lineHeight: 1.5,
                    fontWeight: 700,
                    m: 0,
                  },
                }}
              >
                <ReactMarkdown
                  className={styles.TermsReactMarkdown}
                  components={{
                    li: ({ node, ...props }) => (
                      <Box component="li" sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", mb: 1 }}>
                        <FiberManualRecordIcon sx={{ fontSize: "10px", mr: 0.5, mt: 0.9, color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
                        <Box component="span" sx={{ m: 0, p: 0 }}>
                          {props.children}
                        </Box>
                      </Box>
                    ),
                    ul: ({ node, ...props }) => (
                      <Box component="ul" sx={{ p: 0, m: 0, listStyle: "none", pl: 0, mb: 3 }}>
                        {props.children}
                      </Box>
                    ),
                    h2: ({ node, ...props }) => {
                      let textContent = '';
                      if (Array.isArray(props.children)) {
                        textContent = props.children.map(child => typeof child === 'string' ? child : '').join('');
                      } else if (typeof props.children === 'string') {
                        textContent = props.children;
                      }

                      const startsWithNumber = /^\d+\./.test(textContent.trim());

                      return (
                        <>
                          {startsWithNumber && <Divider sx={{ my: 2, bgcolor: "grey.800" }} />}
                          <Typography component="h2" variant="h3" sx={{ mt: 3, mb: 1 }}>
                            {props.children}
                          </Typography>
                        </>
                      );
                    },
                    p: ({ node, ...props }) => {
                      const processChildren = (children: React.ReactNode): React.ReactNode => {
                        if (typeof children === 'string') {
                          const emailRegex = /(rob@rotogpt\.com)/g;
                          if (emailRegex.test(children)) {
                            const parts = children.split(emailRegex);
                            return parts.map((part, index) =>
                              part === 'rob@rotogpt.com' ? (
                                <Box component="p" key={index} sx={{ fontWeight: "900 !important", color: 'white !important', display: 'inline', opacity: "1 !important" }}>
                                  {part}
                                </Box>
                              ) : part
                            );
                          }
                        }

                        if (Array.isArray(children)) {
                          return children.map((child, idx) => {
                            if (typeof child === 'string') return processChildren(child);
                            if (React.isValidElement(child)) {
                              return React.cloneElement(child, {
                                ...(child.props as any),
                                children: processChildren((child.props as any).children)
                              });
                            }
                            return child;
                          });
                        }

                        return children;
                      };

                      return <p {...props}>{processChildren(props.children)}</p>;
                    },
                  }}
                >
                  {policyText}
                </ReactMarkdown>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      </Box>
      <LegalFooter />
    </Box>
  );
};

export default PrivacyPolicy;
